import { db } from "@/indexedDB/db";
import {
  MediaAttachmentEntity,
  MessageEntity,
  MessageSendingStatus,
} from "../types";

import Dexie from "dexie";
import { MessageCategory } from "@/server/types/message";

// Constants for message categories to avoid repeated Set creation
const MESSAGE_AND_SERVICE_CATEGORIES = new Set([
  MessageCategory.MESSAGE,
  MessageCategory.SERVICE,
]); // [0, 3]

const COMMENT_CATEGORIES = new Set([MessageCategory.COMMENT]); // [5]

// Add message
export const addMessageToLocalDB = async (message: MessageEntity) => {
  return await db.messages.put(message);
};

// Update message by client local id (primary key)
export const updateMessageByClientIdInLocalDB = async (
  _clientId: string,
  message: Partial<MessageEntity>,
) => {
  return await db.messages.update(_clientId, message);
};

// update message by server id (index key)
export const updateMessageByServerIdInLocalDB = async (
  id: string, // server id
  message: Partial<MessageEntity>,
) => {
  const record = await db.messages.where("id").equals(id).first();
  if (!record) {
    return null;
  }
  return await db.messages.update(record._clientId, message);
};

// Remove all messages by chat id
export const removeMessagesByChatIdFromLocalDB = async (chatId: string) => {
  return await db.messages.where("chatId").equals(chatId).delete();
};

/**
 * Reconciliation prune guard: does this chat hold unsent LOCAL-ONLY messages —
 * rows still in sendingStatus PENDING or FAILED that exist ONLY on this device
 * and were never acknowledged by the server?
 *
 * Such messages are the ONLY unrecoverable data during a reconciliation prune:
 * SENT / DELIVERED / READ rows re-sync from the server, but a PENDING/FAILED row
 * lives nowhere else. The orphan/removed-chat reconciliation paths use this to
 * SKIP hard-deleting a chat that would otherwise take unsent messages with it.
 *
 * FAIL-SAFE: on any query error we return `true` ("treat as has-unsent") so a
 * transient Dexie failure can never green-light a destructive prune. Keeping a
 * chat one extra sync cycle is harmless; a wrongly-deleted unsent message is
 * unrecoverable.
 */
export const hasUnsentLocalMessages = async (
  chatId: string,
): Promise<boolean> => {
  try {
    const unsent = await db.messages
      .where("chatId")
      .equals(chatId)
      .filter(
        (msg) =>
          msg.sendingStatus === MessageSendingStatus.PENDING ||
          msg.sendingStatus === MessageSendingStatus.FAILED,
      )
      .first();
    return unsent !== undefined;
  } catch {
    return true;
  }
};

/**
 * Count locally-known messages in a chat that were encrypted under a given CSK
 * epoch. Powers the P2P forward-secrecy trigger: once enough messages accumulate
 * at the current epoch, the client rotates the CSK.
 *
 * `keyEpoch` is not indexed, so this filters in JS over the chat's rows. A
 * missing/null `keyEpoch` denotes the original (pre-rotation) key — epoch 0.
 */
export const countMessagesAtEpochInLocalDB = async (
  chatId: string,
  epoch: number,
): Promise<number> => {
  return await db.messages
    .where("chatId")
    .equals(chatId)
    .filter((msg) => (msg.keyEpoch ?? 0) === epoch)
    .count();
};

// Remove message by local client id (primary key)
export const removeMessageByClientIdFromLocalDB = async (_clientId: string) => {
  return await db.messages.delete(_clientId);
};

// remove  message by server id (index key)
export const removeMessageByServerIdFromLocalDB = async (id: string) => {
  const record = await db.messages.where("id").equals(id).first();
  if (!record) {
    return null;
  }
  return await db.messages.delete(record._clientId);
};

// Bulk add message list
export const addMessagesToLocalDB = async (messageList: MessageEntity[]) => {
  return await db.messages.bulkPut(messageList);
};

// Get messages by chatId
export const getMessagesByChatIdFromLocalDB = async (chatId: string) => {
  return await db.messages.where("chatId").equals(chatId).sortBy("id");
};

// get message by local client id (primary key)
export const getMessageByClientIdFromLocalDB = async (_clientId: string) => {
  return await db.messages.get(_clientId);
};

// get message by server id (index key)
export const getMessageByServerIdFromLocalDB = async (id: string) => {
  const record = await db.messages.where("id").equals(id).first();
  if (!record) {
    return null;
  }
  return await db.messages.get(record._clientId);
};

// Get the latest messages for a chat with a limit
export const getLatestMessagesByChatIdFromLocalDB = async (
  chatId: string,
  limit: number,
) => {
  return await db.messages
    .where("[chatId+id]")
    .between([chatId, Dexie.minKey], [chatId, Dexie.maxKey])
    .reverse()
    .filter((msg) => MESSAGE_AND_SERVICE_CATEGORIES.has(msg.category))
    .limit(limit)
    .toArray();
};

export const getThreadCommentsByChatIdAndMessageIdFromLocalDB = async (
  chatId: string,
  messageId: string,
  limit: number,
) => {
  return await db.messages
    // NOTE: threadId stores the parent thread/message id for all comments
    // Use the [chatId+threadId+id] index to efficiently query comments
    .where("[chatId+threadId+id]")
    .between(
      [chatId, messageId, Dexie.minKey],
      [chatId, messageId, Dexie.maxKey],
    )
    .reverse()
    .filter((msg) => COMMENT_CATEGORIES.has(msg.category))
    .limit(limit)
    .toArray();
};

// Get total message count by chatId
export const getMessageCountByChatIdFromLocalDB = async (chatId: string) => {
  return await db.messages
    .where("chatId")
    .equals(chatId)
    .count()
    .then((count) => count);
};

// Shared helpers for querying messages by attachment type ("media" | "file").
// Reverse before filter so the compound index is traversed newest-first and
// limit() short-circuits as soon as enough matches are found.
type AttachmentType = "media" | "file";

const messageHasAttachment =
  (type: AttachmentType) => (message: MessageEntity) =>
    message.hasAttachment &&
    !!message.content[type] &&
    message.category === MessageCategory.MESSAGE;

const getLatestMessagesWithAttachmentByChatId = async (
  chatId: string,
  limit: number,
  type: AttachmentType,
) =>
  db.messages
    .where("[chatId+id]")
    .between([chatId, Dexie.minKey], [chatId, Dexie.maxKey])
    .reverse()
    .filter(messageHasAttachment(type))
    .limit(limit)
    .toArray();

const getMessagesWithAttachmentCount = (chatId: string, type: AttachmentType) =>
  db.messages
    .where("chatId")
    .equals(chatId)
    .filter(messageHasAttachment(type))
    .count();

export const getLatestMessagesWithMediaByChatIdFromLocalDB = (
  chatId: string,
  limit: number,
) => getLatestMessagesWithAttachmentByChatId(chatId, limit, "media");

/**
 * Media navigation list for the attachment viewer, sourced from the messages
 * table (the source of truth) rather than the download cache — so media whose
 * full file was never downloaded still appears — and scoped to the context the
 * viewer was opened in:
 *   - threadId null → the main chat's messages (category MESSAGE)
 *   - threadId set  → that thread's comments only
 * This scoping is what keeps thread media from leaking into the parent chat's
 * carousel and vice versa. `content.media` is only ever an image or video
 * (documents live on `content.file`), so it doubles as the type filter.
 * Ordered oldest→newest — uuid7 ids sort chronologically, matching the index,
 * so "next" is always the newer item regardless of whether the viewer was
 * opened from a message bubble or the details drawer.
 */
export const getViewerMediaAttachments = async (
  chatId: string,
  threadId: string | null,
): Promise<MediaAttachmentEntity[]> => {
  // Only difference between the two contexts is the index range + which
  // category counts as "in scope"; the media-presence filter is shared.
  const range = threadId
    ? db.messages
        .where("[chatId+threadId+id]")
        .between(
          [chatId, threadId, Dexie.minKey],
          [chatId, threadId, Dexie.maxKey],
        )
    : db.messages
        .where("[chatId+id]")
        .between([chatId, Dexie.minKey], [chatId, Dexie.maxKey]);
  const inScope = threadId
    ? (category: MessageCategory) => COMMENT_CATEGORIES.has(category)
    : (category: MessageCategory) => category === MessageCategory.MESSAGE;

  const scoped = await range
    .filter((msg) => !!msg.content?.media && inScope(msg.category))
    .toArray();

  // In a thread, the parent message (the thread root, whose id IS the threadId)
  // is a main-chat MESSAGE — not a COMMENT — so it isn't in the threaded range
  // above. Prepend it (when it has media) so its attachment shares the thread's
  // carousel instead of escaping into the whole main chat's media. The parent
  // predates its comments, so it belongs first.
  let messages = scoped;
  if (threadId) {
    const parent = await getMessageByServerIdFromLocalDB(threadId);
    // The parent is looked up by global message id, so guard its chatId too:
    // a mismatched/wrong threadId must never prepend another chat's media into
    // this carousel.
    if (parent?.chatId === chatId && parent.content?.media) {
      messages = [parent, ...scoped];
    }
  }

  // Defense in depth: if the optimistic-vs-echo race ever leaves two rows with
  // the same server id (only possible when a payload lacks client_id — legacy
  // rows like that exist), the same attachment would appear twice in the
  // carousel and "next" would step onto its twin. First-seen wins.
  const seenIds = new Set<string>();
  messages = messages.filter((msg) => {
    if (seenIds.has(msg.id)) {
      return false;
    }
    seenIds.add(msg.id);
    return true;
  });

  return messages.map((msg) => {
    const media = msg.content.media!;
    return {
      id: media.attachmentId,
      chatId: msg.chatId,
      messageId: msg.id,
      timestamp: msg.timestamp,
      filename: media.filename,
      fileType: media.fileType,
      isLargeAttachment: media.isLargeAttachment,
      // Carried so the viewer can paint an instant blur for items whose bubble
      // was never on screen (no cached thumbnail yet).
      placeholder: media.placeholder,
      // Native dimensions → the viewer sizes its frame on the first paint, so a
      // small image doesn't flash at viewport size then shrink to native.
      width: media.width,
      height: media.height,
      // Carried so a chunked video reached via arrows/swipe registers its SW
      // stream with the plaintext size and in-progress upload state, exactly
      // like a direct open (useVideoStream needs both while status=uploading).
      fileSize: media.fileSize,
      uploadState: media.uploadState,
    };
  });
};

export const getMessagesWithMediaCountByChatIdFromLocalDB = (chatId: string) =>
  getMessagesWithAttachmentCount(chatId, "media");

export const getLatestMessagesWithFileByChatIdFromLocalDB = (
  chatId: string,
  limit: number,
) => getLatestMessagesWithAttachmentByChatId(chatId, limit, "file");

export const getMessagesWithFileCountByChatIdFromLocalDB = (chatId: string) =>
  getMessagesWithAttachmentCount(chatId, "file");

// Get total message count by between 2 message IDs (for UUID7 time-based ordering)
export const getMessageCountByChatIdAndIdsFromLocalDB = async (
  chatId: string,
  startId: string,
  endId: string | typeof Dexie.maxKey,
) => {
  // Handle Dexie.maxKey case for upper bound
  if (endId === Dexie.maxKey) {
    return await db.messages
      .where("[chatId+id]")
      .between([chatId, startId], [chatId, Dexie.maxKey])
      .count();
  }

  return await db.messages
    .where("[chatId+id]")
    .between([chatId, startId], [chatId, endId])
    .count();
};

export const getCommentCountByThreadIdFromLocalDB = async (
  threadId: string,
) => {
  return await db.messages
    // threadId holds the thread/message id for all comments
    .where("threadId")
    .equals(threadId)
    .count()
    .then((count) => count);
};

/**
 * Deletes a message from the local database with retry capability to handle race conditions.
 *
 * How it works:
 * 1. Checks if the message exists in the local database
 * 2. If found, removes it immediately
 * 3. If not found (due to race condition with creation), implements exponential backoff retry
 * 4. Abandons retry attempts after reaching maximum backoff threshold (1000ms)
 */

// TO DO => Check if this will be needed after we fix the UUID7 generation order (client vs server)
export const deleteMessageFromLocalDBWithRetry = async (
  messageId: string,
  backoffMs: number = 0,
) => {
  if (backoffMs > 1000) {
    return;
  }
  const messageExists = await getMessageByServerIdFromLocalDB(messageId);
  if (messageExists) {
    await removeMessageByClientIdFromLocalDB(messageExists._clientId);
  } else {
    // Retry with exponential backoff or add to a retry queue
    await new Promise((resolve) => setTimeout(resolve, backoffMs));
    await deleteMessageFromLocalDBWithRetry(messageId, backoffMs + 200);
  }
};
